import { cn } from '@ordo/ui/cn'
import { ChevronRight, Flame, FlaskConical, Gift, ShoppingBag, Snowflake, Thermometer } from 'lucide-react'

import { Badge } from '@/components/chefs/badge'
import { sectionRowLines } from '@/lib/chefs/deliverable-sections'
import { formatDeliverableQuantity, temperatureLabel } from '@/lib/chefs/format'
import { packingRowWords } from '@/lib/chefs/packing'
import { formatSupplyLines } from '@/lib/chefs/service-materials'
import type { Deliverable } from '@/lib/chefs/types'
import { dryRunServiceTypeLabel, portionTierTagLabel } from '@/lib/chefs/types'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../shared/chef-packaging-visibility'

interface DeliverableRowProps {
  deliverable: Deliverable
  onClick?: () => void
  selected?: boolean
}

export function DeliverableRow({ deliverable, onClick, selected }: DeliverableRowProps) {
  const TempIcon =
    deliverable.temperature === 'hot' ? Flame : deliverable.temperature === 'ambient' ? Thermometer : Snowflake

  // Packaging → recipe serving size (PROD-173/174), e.g.
  // "Bulk Tray → 2 Pancakes per serving". Either part renders alone. On a
  // multi-item package each serving is prefixed with its item name, which can
  // run long — the sublabel wraps rather than truncating so no portion is lost.
  // A deliverable with SECTIONS (a bag holding a sandwich bag and two cups)
  // names only the outer package here; each section follows on its own line.
  const sections =
    deliverable.sections && deliverable.sections.length > 0 ? sectionRowLines(deliverable.sections) : null
  const sublabel = sections
    ? (deliverable.packaging ?? null)
    : [deliverable.packaging, deliverable.servingSize ? `${deliverable.servingSize} per serving` : null]
        .filter(Boolean)
        .join(' → ')
  const supplies = CHEF_SERVICE_MATERIALS_VISIBLE ? formatSupplyLines(deliverable.supplies) : null
  // Packing (PROD-1500, Figma 156:850): with a packing plan the package
  // description replaces the packaging sublabel and "17 pans" sits under the
  // servings. No plan (a package or portion not sized yet): today's sublabel,
  // no warning (D23).
  const packing =
    deliverable.packing && deliverable.packing.length > 0
      ? packingRowWords(deliverable.packing, deliverable.servingSize)
      : null
  const packedWith =
    !packing && deliverable.packedWith && deliverable.packedWith.length > 0
      ? `Packed with ${deliverable.packedWith.join(', ')}`
      : null

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted/60 focus-visible:bg-muted focus-visible:outline-none',
        selected && 'bg-muted',
      )}>
      <span className="flex w-14 shrink-0 flex-col text-xs font-medium text-muted-foreground">
        <span>{formatDeliverableQuantity(deliverable)}</span>
        {packing?.countLine ? (
          <span data-testid="deliverable-row-container-count" className="font-semibold text-foreground">
            {packing.countLine}
          </span>
        ) : null}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-foreground">{deliverable.name}</span>
          {deliverable.showPortionTier && deliverable.portionTier ? (
            // Two SKUs of one meal (K-8 vs 9-12 portions) in one mealtime —
            // the tier is what tells the packer which row is which. Only set
            // when the block actually has the clash (`markMultiPortionRows`).
            <Badge variant="secondary" className="shrink-0 px-2">
              {portionTierTagLabel(deliverable.portionTier)}
            </Badge>
          ) : null}
          {deliverable.isFieldTrip ? (
            <Badge variant="info" className="shrink-0 gap-1 px-2">
              <ShoppingBag className="h-3 w-3" strokeWidth={1.75} />
              Field trip
            </Badge>
          ) : null}
          {deliverable.isHolidayTreat ? (
            <Badge variant="success" className="shrink-0 gap-1 px-2">
              <Gift className="h-3 w-3" strokeWidth={1.75} />
              Holiday treat · free
            </Badge>
          ) : null}
          {deliverable.isDryRun ? (
            <Badge variant="warning" className="shrink-0 gap-1 px-2">
              <FlaskConical className="h-3 w-3" strokeWidth={1.75} />
              {dryRunServiceTypeLabel(deliverable.dryRunServiceType)}
            </Badge>
          ) : null}
        </span>
        {packing ? (
          <>
            <span data-testid="deliverable-row-packing" className="text-xs font-medium text-foreground">
              {packing.title}
            </span>
            {packing.detail ? <span className="text-xs text-muted-foreground">{packing.detail}</span> : null}
          </>
        ) : sublabel ? (
          <span className="text-xs text-muted-foreground">{sublabel}</span>
        ) : null}
        {!packing && sections ? (
          <span data-testid="deliverable-row-sections" className="flex flex-col text-xs text-muted-foreground">
            {sections.map((line) => (
              <span key={line} className="pl-3 before:mr-1 before:content-['›']">
                {line}
              </span>
            ))}
          </span>
        ) : null}
        {packedWith ? <span className="text-xs text-muted-foreground">{packedWith}</span> : null}
        {deliverable.condiments ? (
          <span className="truncate text-xs text-muted-foreground">{deliverable.condiments}</span>
        ) : null}
      </span>

      {supplies ? (
        // SUPPLIES column — disposables riding this package (trays/boats,
        // cutlery, plates), kept apart from the packaging sublabel above.
        <span className="hidden w-48 shrink-0 flex-col text-xs text-muted-foreground lg:flex" title={supplies}>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">Supplies</span>
          <span className="truncate">{supplies}</span>
        </span>
      ) : null}

      <Badge variant={deliverable.temperature} className="gap-1 px-2">
        <TempIcon className="h-3 w-3" strokeWidth={1.75} />
        {temperatureLabel(deliverable.temperature)}
      </Badge>

      <ChevronRight
        className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-foreground"
        strokeWidth={1.5}
      />
    </button>
  )
}
